import * as React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { makeStyles } from '@material-ui/core/styles';
import { ActionContext } from '../../context/ActionContext';
import { CMIC_TREE_NODE_TYPES, DOC_PAGE_LIMIT } from '../../shared/Constants';
import { CmicContext } from '../../context/CmicContext';
import { CURRENT_OFFICE_THEME, THEME_ID, baseFontFamilyStyle, handleTextTheme } from '../../shared/styles';
import { COLORS } from '../../utility/tasks/colors';
import { isBGClrDark } from '../../utility/UtilityFunctions';
import CustomTextInput from '../../components/common/CustomTextInput';
import AsyncSelect from 'react-select/async';
import FormControl from '@mui/material/FormControl';
import { disabledCustomStylesDropbox } from '../../shared/styles';
import { DatePicker, defaultDatePickerStrings, Stack, Dropdown, TextField } from '@fluentui/react';
import PMObjectDetail from './components/PMObjectDetail';
import Divider from '@mui/material/Divider';
import { getPMDocument } from '../../utility/fetchData';
import { formatDate } from '../../shared/UtilityFunctions';

export const isShowMetaPanelData = nodeType => {
	if (
		nodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI ||
		nodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE ||
		nodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL ||
		nodeType === CMIC_TREE_NODE_TYPES.PROJECT_PCI ||
		nodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS ||
		nodeType === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION
	)
		return true;

	return false;
};

const MetaDataPanel = ({ data }) => {
	const { currentOfficeTheme, userInfo } = React.useContext(CmicContext);
	const { setIsLoading, nodeData, activeNode } = React.useContext(ActionContext);
	const scrollableRef = React.useRef(null);
	const dataFetched = React.useRef(false);
	const pageInfo = React.useRef(0);
	const hasMoreFlag = React.useRef(true);
	const uploadFromEmailFlag = React.useRef(false);

	const [PmObjectAttachments, setPmObjectAttachments] = React.useState({});

	React.useEffect(() => {
		if (!(pageInfo.current !== 0)) {
			uploadFromEmailFlag.current = true;
		}
		pageInfo.current = 0;
		hasMoreFlag.current = true;
		dataFetched.current = false;
		if (nodeData?.items) {
			setPmObjectAttachments([...nodeData?.items]);
		}
	}, [nodeData]);

	React.useEffect(() => {
		setPmObjectAttachments([...nodeData?.items]);
		const scrollableElement = scrollableRef.current;

		if (!scrollableElement) {
			return;
		}

		if (scrollableElement) {
			scrollableElement.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (scrollableElement) {
				scrollableElement.removeEventListener('scroll', handleScroll);
			}
		};
	}, [scrollableRef.current]);

	const isBottomOfElement = () => {
		return (
			scrollableRef.current.scrollTop + scrollableRef.current.clientHeight >=
			scrollableRef.current.scrollHeight - 2
		);
	};

	const fetchData = async () => {
		dataFetched.current = true;

		if ((nodeData.hasMore || uploadFromEmailFlag.current) && hasMoreFlag.current) {
			pageInfo.current = pageInfo.current + 1;
			setIsLoading(true);
			if (nodeData?.items.length) {
				const objVUuid = nodeData?.items[0].SysrdObjectVUuid;
				const result = await getPMDocument(userInfo, objVUuid, activeNode, {
					offset: pageInfo.current * DOC_PAGE_LIMIT,
					limit: DOC_PAGE_LIMIT,
					orderBy: '',
				});
				if (result) {
					hasMoreFlag.current = result?.data?.hasMore;
					setPmObjectAttachments(prev => {
						return [...prev, ...result?.data?.items];
					});
				}
				setIsLoading(false);
				dataFetched.current = false;
			} else {
				setIsLoading(false);
			}
		}
	};

	const handleScroll = () => {
		if (isBottomOfElement() && !dataFetched.current) {
			fetchData();
		}
	};

	const showFieldData = fieldData => {
		if (fieldData) {
			return fieldData;
		}
		return ' - ';
	};

	const useStyles = makeStyles(() => ({
		richTextEditor: {
			'& .ck-editor__main > .ck-editor__editable': {
				paddingLeft: '2px',
				backgroundColor: 'transparent',
				color:
					currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
						? currentOfficeTheme.bodyForegroundColor
						: COLORS.OUTLOOK_BLACK_FONT,
				borderWidth: 0,
			},
			'& .ck': {
				backgroundColor: 'transparent',
				borderWidth: 0,
				color: currentOfficeTheme.bodyForegroundColor,
			},
			'& .ck.ck-editor__editable_inline>:first-child': {
				marginTop: '0px',
			},
		},
	}));

	const classes = useStyles();

	const columnValueStyle = {
		padding: '2px 2px 2px 6px',
		color: currentOfficeTheme.bodyForegroundColor,
	};

	const headerTextStyle = {
		fontWeight: 'normal',
		fontFamily: baseFontFamilyStyle,
		fontSize: '14px',
		// color: isBGClrDark() ? currentOfficeTheme.bodyForegroundColor : COLORS.OUTLOOK_BLACK_FONT,
		color:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? currentOfficeTheme.bodyForegroundColor
				: COLORS.LABEL_HEADER_TEXT_COLOR,
		margin: '0.2rem 8px',
	};

	const containerStyle = {
		display: 'flex-block',
		flex: 1,
		//paddingTop: "1rem",
		overflowY: 'scroll',
		background:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? currentOfficeTheme.bodyBackgroundColor
				: '#faf9f8',
	};

	const textInputStyles = {
		fieldGroup: {
			width: '100%',
			minHeight: '14px',
			maxHeight: '14px',
			marginBottom: '16px',
			marginTop: '5px',
			background: 'tranparent',
			color: isBGClrDark() ? currentOfficeTheme.bodyForegroundColor : COLORS.OUTLOOK_BLACK_FONT,
			':hover': {
				border: 'none',
			},
			border: 'none',
		},
		field: {
			color: isBGClrDark()
				? `${currentOfficeTheme.bodyForegroundColor} !important`
				: `${COLORS.OUTLOOK_BLACK_FONT} !important`,
			fontFamily: baseFontFamilyStyle,
			border: 'none',
			alignSelf: 'center',
			padding: '0 8px',
			':disabled': {
				color: 'currentColor !important',
				background: 'transparent',
			},
		},

		subComponentStyles: {
			label: getLabelStyle,
		},
	};

	const getLabelStyle = props => {
		return {
			root: {
				color: currentOfficeTheme.bodyForegroundColor,
				fontFamily: 'Segoe UI',
			},
		};
	};

	const datePickerStyles = {
		callout: {
			selectors: {
				'& button': {
					backgroundColor: 'transparent',
					color: currentOfficeTheme.bodyForegroundColor,
				},
			},
		},
		root: {
			backgroundColor: 'transparent',
		},

		textField: {
			pointerEvents: 'none',
			'& .ms-Label': {
				color: currentOfficeTheme.bodyForegroundColor,
			},
			'& .ms-TextField-fieldGroup': {
				border: 'none',
				backgroundColor: 'transparent',
				color: currentOfficeTheme.bodyForegroundColor,
				':after': {
					borderColor: 'transparent',
				},
			},
			'& .ms-TextField-field': {
				color: isBGClrDark()
					? `${currentOfficeTheme.bodyForegroundColor} !important`
					: `${COLORS.OUTLOOK_BLACK_FONT} !important`,
			},
			'& input[readonly]': {
				color:
					currentOfficeTheme.themeId === THEME_ID.BLACK
						? `${currentOfficeTheme.bodyForegroundColor} !important`
						: `${CURRENT_OFFICE_THEME.bodyForegroundColor} !important`,
				backgroundColor: 'transparent',
			},
		},
		icon: {
			color: currentOfficeTheme.bodyForegroundColor,
			display: 'none',
		},
	};

	const datePickerCalendarStyles = {
		styles: {
			'& ms-DatePicker-event--without-label': {
				display: 'none !important',
			},
			root: {
				backgroundColor: 'transparent',
				color: currentOfficeTheme.bodyForegroundColor,
			},
		},
		calendarDayProps: {
			styles: {
				dayCell: {
					color: currentOfficeTheme.bodyForegroundColor,
					':focus': {
						backgroundColor: currentOfficeTheme.bodyForegroundColor,
						color: currentOfficeTheme.bodyBackgroundColor,
					},
					':hover': {
						backgroundColor: currentOfficeTheme.bodyForegroundColor,
						color: currentOfficeTheme.bodyBackgroundColor,
					},
				},
			},
		},
	};

	const text_theme = handleTextTheme({ themeId: currentOfficeTheme.themeId });

	const CustomDatePicker = ({ date, title }) => {
		return (
			<FormControl sx={{ minWidth: 120, width: '45%', marginRight: '5px', marginBottom: '5px' }} size="small">
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<label
						aria-label="Required Date"
						style={{ ...headerTextStyle, margin: '0.2rem 8px 0px 8px' }}
						htmlFor="Created Date"
					>
						{' '}
						{title}{' '}
					</label>
				</div>
				<DatePicker
					allowTextInput={false} // This prevents typing into the text box
					firstWeekOfYear={1}
					showMonthPickerAsOverlay
					styles={datePickerStyles}
					calendarProps={datePickerCalendarStyles}
					id="Required Date"
					aria-label="Required Date"
					autoAdjustHeight
					placeholder="Select a date..."
					showGoToToday
					ariaLabel="Select a date"
					strings={defaultDatePickerStrings}
					value={new Date(date)}
					formatDate={formatDate}
				/>
			</FormControl>
		);
	};

	const CustomDropbox = ({ data, title }) => {
		return (
			<>
				<FormControl sx={{ width: '100%', marginBottom: '5px' }} size="small">
					<label aria-label="From" style={headerTextStyle} htmlFor="From">
						{title}
					</label>
					<AsyncSelect
						options={{}}
						value={{ key: data?.data, label: showFieldData(data?.data) }}
						placeholder="Please select a Project Contact"
						label="From"
						name="From"
						aria-label="Please select a Project Contact"
						isSearchable
						required
						isDisabled
						components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
						styles={disabledCustomStylesDropbox(currentOfficeTheme)}
					/>
				</FormControl>
			</>
		);
	};

	const CustomDivider = () => {
		return (
			<Divider
				style={{
					maxHeight: '0.1px',
					background:
						currentOfficeTheme.themeId === THEME_ID.BLACK ||
						currentOfficeTheme.themeId === THEME_ID.DARK_GREY
							? '#353535'
							: 'rgb(128,128,128, 0.4)',
				}}
			/>
		);
	};

	if (CMIC_TREE_NODE_TYPES.PROJECT_RFI === activeNode && data) {
		return (
			<div className="addToExistingPMDOC" ref={scrollableRef} style={containerStyle}>
				<CustomTextInput
					currentOfficeTheme={currentOfficeTheme}
					fieldInfo={{ value: showFieldData(data?.PmscrfiSubject) }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Subject"
					readOnly={true}
				/>
				<CustomDropbox
					data={{ data: data?.PmscrfiStatusDesc }}
					fieldInfo={{ value: data?.PmscrfiStatusDesc, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Status"
					isDisabled={true}
				/>
				<CustomDatePicker date={data.PmscrfiCreateDate} title="Created Date" />
				<CustomDatePicker date={data.PmscrfiRequiredDate} title="Required Date" />
				<CustomDropbox
					data={{ data: data?.PmscrfiFromContactName }}
					fieldInfo={{ value: data?.PmscrfiFromContactName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="From"
					isDisabled={true}
				/>
				<CustomDropbox
					data={{ data: data?.PmscrfiToContactName }}
					fieldInfo={{ value: data?.PmscrfiFromContactName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="To"
					isDisabled={true}
				/>
				<FormControl
					sx={{
						width: '100%',
					}}
					size="small"
				>
					<label aria-label="Question" style={headerTextStyle} htmlFor="question">
						Question
					</label>
					<div style={columnValueStyle} className={classes.richTextEditor}>
						<CKEditor
							editor={ClassicEditor}
							config={{
								removePlugins: ['Heading'],
								toolbar: [],
							}}
							style={{ margin: '0px', padding: '0px' }}
							data={showFieldData(data?.PmscrfiQuestion)}
							ariaLabel="Question field"
							name="question"
							id="question"
							disabled
							disableWatchdog
						/>
					</div>
				</FormControl>
				{/* <CustomDivider /> */}
				<PMObjectDetail documents={PmObjectAttachments} currentOfficeTheme={currentOfficeTheme} />
			</div>
		);
	}

	if (CMIC_TREE_NODE_TYPES.PROJECT_ISSUE === activeNode && data) {
		return (
			<div className="addToExistingPMDOC" ref={scrollableRef} style={containerStyle}>
				<CustomTextInput
					currentOfficeTheme={currentOfficeTheme}
					fieldInfo={{ value: data?.DmiTitle }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Subject"
					readOnly={true}
				/>
				<CustomDropbox
					data={{ data: data.DmiStatusDesc }}
					fieldInfo={{ value: data?.DmiStatusDesc, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Status"
					isDisabled={true}
				/>
				<FormControl sx={{ minWidth: 120, width: '45%', marginRight: '5px', marginBottom: '5px' }} size="small">
					<CustomDropbox
						data={{ data: data.DmiTypeCode }}
						fieldInfo={{ value: data?.DmiTypeCode, name: 'name' }}
						textInputStyles={textInputStyles}
						headerTextStyle={headerTextStyle}
						className={text_theme}
						title="Type"
						isDisabled={true}
					/>
				</FormControl>
				<FormControl sx={{ minWidth: 120, width: '45%', marginRight: '5px', marginBottom: '5px' }} size="small">
					<CustomDropbox
						data={{ data: data.DmiPriorityDesc }}
						fieldInfo={{ value: data?.DmiPriorityDesc, name: 'name' }}
						textInputStyles={textInputStyles}
						headerTextStyle={headerTextStyle}
						className={text_theme}
						title="Severity"
						isDisabled={true}
					/>
				</FormControl>
				<CustomDatePicker date={data.DmiIuCreateDate} title="Created Date" />
				<CustomDatePicker date={data.DmiDueDate} title="Required Date" />
				<CustomDropbox
					data={{ data: data.DmiContactName }}
					fieldInfo={{ value: data?.DmiContactName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="From"
					isDisabled={true}
				/>
				<CustomDropbox
					data={{ data: data.DmiRespContactName }}
					fieldInfo={{ value: data?.DmiRespContactName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="To"
					isDisabled={true}
				/>
				<FormControl
					sx={{
						width: '100%',
					}}
					size="small"
				>
					<label aria-label="Question" style={headerTextStyle} htmlFor="question">
						Question
					</label>
					<div style={columnValueStyle} className={classes.richTextEditor}>
						<CKEditor
							editor={ClassicEditor}
							config={{
								removePlugins: ['Heading'],
								toolbar: [],
							}}
							style={{ margin: '0px', padding: '0px' }}
							data={showFieldData(data?.DmiDescription)}
							ariaLabel="Question field"
							name="question"
							id="question"
							disabled
							disableWatchdog
						/>
					</div>
				</FormControl>
				{/* <CustomDivider /> */}
				<PMObjectDetail documents={PmObjectAttachments} currentOfficeTheme={currentOfficeTheme} />
			</div>
		);
	}

	if (CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS === activeNode && data) {
		return (
			<div className="addToExistingPMDOC" ref={scrollableRef} style={containerStyle}>
				<CustomTextInput
					currentOfficeTheme={currentOfficeTheme}
					fieldInfo={{ value: data?.PmsmSbmtName }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Name"
					readOnly={true}
				/>
				<CustomDatePicker date={data.PmsmIuCreateDate} title="Created Date" />
				<CustomDropbox
					data={{ data: data.PmsmSbmtStatusName }}
					fieldInfo={{ value: data?.PmsmSbmtStatusName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Status"
					isDisabled={true}
				/>
				<CustomDropbox
					data={{ data: data.PmsmTypeDesc }}
					fieldInfo={{ value: data?.PmsmTypeDesc, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Type"
					isDisabled={true}
				/>
				{/* <CustomDivider /> */}
				<PMObjectDetail documents={PmObjectAttachments} currentOfficeTheme={currentOfficeTheme} />
			</div>
		);
	}

	if (CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL === activeNode && data) {
		return (
			<>
				<div className="addToExistingPMDOC" ref={scrollableRef} style={containerStyle}>
					<CustomDropbox
						data={{ data: data.PmjRecordStatus }}
						fieldInfo={{ value: data?.PmjRecordStatus, name: 'name' }}
						textInputStyles={textInputStyles}
						headerTextStyle={headerTextStyle}
						className={text_theme}
						title="Record Status"
						isDisabled={true}
					/>
					<CustomDatePicker date={data.PmjDate} title="Created Date" />
					<CustomDropbox
						data={{ data: data.PmjPrepContactName }}
						fieldInfo={{ value: data?.PmjPrepContactName, name: 'name' }}
						textInputStyles={textInputStyles}
						headerTextStyle={headerTextStyle}
						className={text_theme}
						title="Prepared By"
						isDisabled={true}
					/>
					<CustomTextInput
						currentOfficeTheme={currentOfficeTheme}
						fieldInfo={{ value: data?.PmjRainDayFlag }}
						textInputStyles={textInputStyles}
						headerTextStyle={headerTextStyle}
						className={text_theme}
						title="Rainy Day"
						readOnly={true}
					/>
					{/* <CustomDivider /> */}
					<PMObjectDetail documents={PmObjectAttachments} currentOfficeTheme={currentOfficeTheme} />
				</div>
			</>
		);
	}

	if (CMIC_TREE_NODE_TYPES.PROJECT_PCI === activeNode && data) {
		return (
			<div className="addToExistingPMDOC" ref={scrollableRef} style={containerStyle}>
				<CustomTextInput
					currentOfficeTheme={currentOfficeTheme}
					fieldInfo={{ value: showFieldData(data?.CmmName) }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Subject"
					readOnly={true}
				/>
				<CustomDropbox
					data={{ data: data?.CmmStatName }}
					fieldInfo={{ value: data?.CmmStatName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Status"
					isDisabled={true}
				/>
				<CustomDatePicker date={data.CmmRefDate} title="Date" />
				<CustomDropbox
					data={{ data: data.CmmTypeName }}
					fieldInfo={{ value: data?.CmmTypeName, name: 'name' }}
					textInputStyles={textInputStyles}
					headerTextStyle={headerTextStyle}
					className={text_theme}
					title="Type"
					isDisabled={true}
				/>

				<PMObjectDetail documents={PmObjectAttachments} currentOfficeTheme={currentOfficeTheme} />
			</div>
		);
	}

	return null;
};

export default MetaDataPanel;
